/**
 * Network error detection and user-facing messages
 *
 * Use when handling fetch/Supabase failures to show a consistent "no network" experience.
 *
 * Optional pre-check before server actions (for instant feedback):
 *   if (!isOnline()) { toast.error(NETWORK_ERROR_MESSAGE); return; }
 * Then in .catch(): if (isNetworkError(e)) toast.error(NETWORK_ERROR_MESSAGE); else ...
 */

const NETWORK_ERROR_PATTERNS = [
  "failed to fetch",
  "network error",
  "err_network",
  "econnrefused",
  "etimedout",
  "load failed",
  "network request failed",
  "connection refused",
  "offline",
] as const;

/**
 * Returns true if the error is likely due to no network / unreachable server.
 * Handles both Error instances and string messages (e.g. from server action results).
 */
export function isNetworkError(error: unknown): boolean {
  if (error == null) return false;
  const message =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? error.message
        : String(error);
  const lower = message.toLowerCase();
  return NETWORK_ERROR_PATTERNS.some((p) => lower.includes(p));
}

/** User-facing message for no-network cases. Single source of truth for copy. */
export const NETWORK_ERROR_MESSAGE =
  "No connection. Check your network and try again.";

/**
 * Returns a user-friendly message for network errors, or the original message otherwise.
 * Use when you want to show one consistent line for network issues but keep other errors as-is.
 */
export function getNetworkErrorMessage(error?: unknown): string {
  if (error != null && isNetworkError(error)) {
    return NETWORK_ERROR_MESSAGE;
  }
  return typeof error === "string" ? error : error instanceof Error ? error.message : "Something went wrong.";
}
